import { useCallback, useEffect, useMemo, useState } from 'react';
import { getMyBrands } from '../services/brandsService';
import { findDefaultBrand } from '../Utils/brandForm';

/**
 * Identidades del JWT (`GET /getmybrands`). Cuelgan del usuario, no de una
 * empresa: 0, 1 o N. `can_create_brand` es siempre true si el GET sale bien.
 *
 * @param {{ enabled?: boolean }} [options]
 */
export function useMyBrands({ enabled = true } = {}) {
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState('');
  const [brands, setBrands] = useState([]);
  const [canCreateBrand, setCanCreateBrand] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const { signal } = controller;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const brandsRes = await getMyBrands({ signal });
        if (signal.aborted) return;
        if (!brandsRes.ok) {
          setBrands([]);
          setCanCreateBrand(false);
          setError(brandsRes.message || 'No se pudieron cargar tus identidades.');
          return;
        }
        setBrands(brandsRes.brands);
        setCanCreateBrand(brandsRes.canCreateBrand);
      } catch (err) {
        if (err?.name === 'AbortError') return;
        console.error('Marcas: no se pudo cargar el estado inicial', err);
        setError('No se pudo hablar con el servidor.');
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [enabled, reloadKey]);

  const defaultBrand = useMemo(() => findDefaultBrand(brands), [brands]);

  return {
    loading,
    error,
    brands,
    canCreateBrand,
    defaultBrand,
    reload,
  };
}

export default useMyBrands;
